<script setup lang="ts">
import { computed, onMounted, ref, shallowRef } from "vue";
import { useRoute, useRouter } from "vue-router";

import DraggableList from "@/components/ui/DraggableList.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import SkoreSpinner from "@/components/ui/SkoreSpinner.vue";
import { type EstimatorReportDto, type PresentationDto, type PresentationItemDto } from "@/dto";
import LayoutWithSidebar from "@/layouts/LayoutWithSidebar.vue";
import { RouteNames } from "@/router";
import { useApi } from "@/services/api";
import { useModalsStore } from "@/stores/modals";
import { useToastsStore } from "@/stores/toasts";
import EstimatorReport from "@/views/EstimatorReport.vue";
import ProjectDetailViewPresentationsItem from "@/views/projects/ProjectDetailViewPresentationsItem.vue";

const api = useApi();
const route = useRoute();
const router = useRouter();
const modals = useModalsStore();
const toast = useToastsStore();
const isLoadingPresentationsList = ref(true);
const presentationsList = shallowRef<PresentationDto[]>([]);
const selectedPresentationId = ref(-1);
const isLoadingPresentation = ref(true);
const presentation = shallowRef<PresentationDto | null>(null);
const presentationItems = ref<PresentationItemDto[]>([]);
const isDrawerOpen = ref(false);
const isDrawerLoading = ref(false);
const report = shallowRef<EstimatorReportDto>();

const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);

async function newPresentation() {
  const [confirmed, name] = await modals.prompt({
    title: "New presentation",
    message: "Give a name to your presentation.",
  });
  if (confirmed && name) {
    await createPresentation(name);
  }
}

async function fetchPresentationList() {
  isLoadingPresentationsList.value = true;
  const response = await api.listProjectPresentations(tenantName.value, projectName.value);
  if (response !== null) {
    presentationsList.value = response;
  }
  isLoadingPresentationsList.value = false;
}

async function setSelectedPresentation(id: number) {
  if (presentationsList.value.find((p) => p.id === id) === undefined) {
    router.push({ name: RouteNames.NotFound });
  } else {
    selectedPresentationId.value = id;

    const { href } = router.resolve({
      name: RouteNames.ProjectDetail,
      params: {
        tenantName: tenantName.value,
        projectName: projectName.value,
        section: "presentations",
        itemId: id,
      },
    });
    history.pushState({}, "", href);

    await fetchPresentation();
  }
}

async function createPresentation(name: string) {
  isLoadingPresentationsList.value = true;
  const response = await api.createProjectPresentation(tenantName.value, projectName.value, name);
  if (response !== null) {
    await fetchPresentationList();
  }
}

async function fetchPresentation() {
  isLoadingPresentation.value = true;
  presentation.value = await api.fecthProjectPresentation(
    tenantName.value,
    projectName.value,
    selectedPresentationId.value
  );
  if (presentation.value !== null) {
    // fetch presentation items
    const items = await api.listPresentationItems(
      tenantName.value,
      projectName.value,
      selectedPresentationId.value
    );
    presentationItems.value = items;
  }

  isLoadingPresentation.value = false;
}

async function savePresentation() {
  await api.setProjectPresentationContent(
    tenantName.value,
    projectName.value,
    selectedPresentationId.value,
    presentationItems.value.map((i) => i.id)
  );
}

async function updateItemNote(id: number, note: string) {
  await api.updatePresentationItemNote(
    tenantName.value,
    projectName.value,
    selectedPresentationId.value,
    id,
    note
  );
}

async function openEstimatorReportInDrawer(reportId: number) {
  isDrawerOpen.value = true;
  isDrawerLoading.value = true;
  const r = await api.fetchEstimatorReport(tenantName.value, projectName.value, reportId);
  if (r === null) {
    toast.addToast("Report not found", "error", { duration: 5 });
  } else {
    report.value = r;
    isDrawerLoading.value = false;
  }
}

function closeDrawer() {
  isDrawerOpen.value = false;
  isDrawerLoading.value = false;
  report.value = undefined;
}

async function deletePresentationItem(itemId: number) {
  if (presentation.value) {
    const confirmed = await modals.confirm({
      title: "Delete presentation item",
      message: `Do you really want to remove this item from presentation ${presentation.value.name} ?`,
    });
    if (confirmed) {
      await api.deletePresentationItem(
        tenantName.value,
        projectName.value,
        presentation.value.id,
        itemId
      );
      await fetchPresentation();
    }
  }
}

async function deletePresentation() {
  if (presentation.value !== null) {
    const confirmed = await modals.confirm({
      title: "Delete presentation",
      message: `Do you really want to delete presentation ${presentation.value.name} ?`,
    });
    if (confirmed) {
      isLoadingPresentationsList.value = true;
      await api.deletePresentation(tenantName.value, projectName.value, presentation.value.id);
      presentation.value = null;
      selectedPresentationId.value = -1;
      await fetchPresentationList();
    }
  }
}

onMounted(async () => {
  await fetchPresentationList();

  const routeId = parseInt(route.params.itemId as string);
  if (!isNaN(routeId)) {
    await setSelectedPresentation(routeId);
  } else {
    if (presentationsList.value.length > 0) {
      await setSelectedPresentation(presentationsList.value[0].id);
    }
  }
});
</script>

<template>
  <div class="presentations">
    <LayoutWithSidebar>
      <template #sidebar>
        <div class="presentations-list">
          <div class="title">
            <span class="label-s">Presentations</span>
            <span class="actions">
              <SimpleButton
                v-if="presentation !== null"
                icon-left="icon-trash"
                kind="tertiary"
                @click="deletePresentation"
              />
              <SimpleButton icon-left="icon-plus" kind="primary" @click="newPresentation" />
            </span>
          </div>
          <Transition name="fade" mode="out-in">
            <div v-if="isLoadingPresentationsList" class="loading">
              <SkoreSpinner />
            </div>
            <div class="list" v-else>
              <div
                v-for="p in presentationsList"
                :key="p.id"
                class="link paragraph-s"
                :class="{ selected: p.id === selectedPresentationId }"
                @click="setSelectedPresentation(p.id)"
              >
                <i class="icon icon-book" />
                <span>{{ p.name }}</span>
                <i class="icon selected-indicator icon-chevron-right" />
              </div>
            </div>
          </Transition>
        </div>
      </template>
      <template #content>
        <div class="presentation-container">
          <div v-if="isLoadingPresentation" class="loading">
            <SkoreSpinner />
          </div>
          <div
            v-else-if="
              presentation !== null &&
              presentation.content.length > 0 &&
              presentationsList.length > 0
            "
            class="presentation"
          >
            <DraggableList
              v-model:items="presentationItems"
              auto-scroll-container-selector=".presentation-container"
              @dragover.prevent
              @reorder="savePresentation"
            >
              <template #item="{ id, name, note, media_type, value, report_id, comparison_id }">
                <ProjectDetailViewPresentationsItem
                  :id="id"
                  :name="name"
                  :note="note"
                  :report_id="report_id"
                  :comparison_id="comparison_id"
                  :media_type="media_type"
                  :value="value"
                  :presentation-id="selectedPresentationId"
                  @open-estimator-report="openEstimatorReportInDrawer"
                  @delete="deletePresentationItem"
                  @save-note="updateItemNote"
                />
              </template>
            </DraggableList>
          </div>
          <div v-else class="empty">
            <div class="label-l">No items</div>
            <div class="subtitle paragraph-s">
              No items found. Try to add items from your experimentations first.
            </div>
          </div>
        </div>
      </template>
    </LayoutWithSidebar>
  </div>

  <Teleport to="body">
    <Transition name="drawer-appear">
      <div v-if="isDrawerOpen" class="report-drawer">
        <div class="outside-click-catcher" @click="closeDrawer" />
        <div class="report-drawer-content">
          <div class="close">
            <SimpleButton icon-left="icon-close" kind="ghost" @click="closeDrawer" />
          </div>
          <SkoreSpinner v-if="isDrawerLoading" />
          <EstimatorReport v-else-if="report !== undefined" :report="report" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.presentations {
  height: 100%;

  .layout-with-sidebar {
    height: 100%;
  }

  .presentations-list {
    padding: var(--spacing-8);

    .loading {
      padding: var(--spacing-8);
      text-align: center;
    }

    .title {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;

      .actions {
        display: flex;
        flex-direction: row;
        gap: var(--spacing-4);
      }
    }

    .list {
      margin-top: var(--spacing-20);

      .link {
        display: flex;
        height: 33px;
        flex-direction: row;
        align-items: center;
        padding: var(--spacing-4);
        border-radius: var(--radius-xs);
        margin: var(--spacing-4) 0;
        cursor: pointer;
        gap: var(--spacing-8);
        transition: background-color var(--animation-duration) var(--animation-easing);

        span {
          overflow: hidden;
          flex: 1;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .selected-indicator {
          color: var(--color-text-secondary);
          opacity: 0;
          transition: opacity var(--animation-duration) var(--animation-easing);
        }

        &:hover,
        &.selected {
          background-color: var(--color-background-secondary);

          .selected-indicator {
            opacity: 1;
          }
        }
      }
    }
  }

  .presentation-container {
    --min-height: calc(var(--content-height) - var(--spacing-20) - (var(--spacing-12) * 2) - 2px);

    min-height: var(--min-height);
    padding: var(--spacing-12) var(--spacing-16) var(--spacing-12) var(--spacing-6);

    .loading,
    .empty {
      display: flex;
      height: var(--min-height);
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    .empty {
      text-align: center;

      & > * {
        max-width: 30dvw;
      }

      .subtitle {
        color: var(--color-text-secondary);
      }
    }
  }
}

.report-drawer {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  display: flex;
  width: 100dvw;
  height: 100dvh;
  justify-content: flex-end;
  background-color: rgb(0 0 0 / 5%);
  transition: background-color var(--animation-duration) var(--animation-easing);

  .outside-click-catcher {
    position: absolute;
    z-index: 1;
    width: 100%;
    height: 100%;
  }

  .report-drawer-content {
    z-index: 2;
    width: 70dvw;
    height: 100%;
    padding: var(--spacing-24);
    background-color: var(--color-background-primary);
    transition: transform var(--animation-duration) var(--animation-easing);

    .close {
      text-align: right;
    }
  }
}

.drawer-appear-enter-from,
.drawer-appear-leave-to {
  background-color: transparent;

  .report-drawer-content {
    transform: translateX(100%);
  }
}
</style>
